/**
 * Wound Assessment Documentation Component
 * 
 * AI-powered wound assessment assistant for hospice care.
 * Supports image upload, AI analysis, structured documentation, and note generation.
 */

import React, { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Camera,
  Upload,
  FileImage,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Brain,
  Sparkles,
  ClipboardList,
  FileText,
  Activity,
  ChevronRight,
  AlertCircle,
  Info,
  X
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Wound type options
const WOUND_TYPES = [
  { id: "pressure_injury", name: "Pressure Injury", requiresStaging: true },
  { id: "skin_tear", name: "Skin Tear", requiresStaging: false },
  { id: "surgical", name: "Surgical Wound", requiresStaging: false },
  { id: "arterial", name: "Arterial Ulcer", requiresStaging: false },
  { id: "venous", name: "Venous Ulcer", requiresStaging: false },
  { id: "diabetic", name: "Diabetic Ulcer", requiresStaging: false },
  { id: "malignant", name: "Malignant Wound", requiresStaging: false },
  { id: "moisture", name: "Moisture-Associated Skin Damage", requiresStaging: false },
  { id: "other", name: "Other", requiresStaging: false }
];

const PRESSURE_STAGES = [
  { id: "1", name: "Stage 1", desc: "Non-blanchable erythema" },
  { id: "2", name: "Stage 2", desc: "Partial-thickness skin loss" },
  { id: "3", name: "Stage 3", desc: "Full-thickness skin loss" },
  { id: "4", name: "Stage 4", desc: "Full-thickness tissue loss" },
  { id: "unstageable", name: "Unstageable", desc: "Obscured by slough/eschar" },
  { id: "dti", name: "Deep Tissue Injury", desc: "Persistent discoloration" }
];

const WOUND_BED_TYPES = [
  { id: "granulation", name: "Granulation", color: "bg-red-500" },
  { id: "slough", name: "Slough", color: "bg-yellow-400" },
  { id: "eschar", name: "Eschar", color: "bg-gray-800" },
  { id: "epithelializing", name: "Epithelializing", color: "bg-pink-300" },
  { id: "hypergranulation", name: "Hypergranulation", color: "bg-red-700" }
];

const DRAINAGE_AMOUNTS = ["none", "scant", "moderate", "heavy"];
const DRAINAGE_TYPES = ["serous", "serosanguinous", "sanguinous", "purulent"];

const SURROUNDING_SKIN = [
  "intact", "erythematous", "macerated", "indurated", 
  "warm", "edematous", "excoriated", "dry/flaky"
];

const HOSPICE_FOCUS = [
  { id: "comfort_focused", name: "Comfort-Focused", desc: "Wound care aligned with comfort goals" },
  { id: "actively_treated", name: "Actively Treated", desc: "Active wound treatment within hospice POC" },
  { id: "declining_expected", name: "Decline Expected", desc: "Wound decline expected with disease progression" }
];

export default function WoundAssessment({ patientId, patientName, onComplete, onClose }) {
  const [activeTab, setActiveTab] = useState("upload");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Image state
  const [imageData, setImageData] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  
  // AI Analysis state
  const [aiAnalysis, setAiAnalysis] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    // Location
    anatomicalSite: "",
    bodyRegion: "",
    laterality: "",
    
    // Measurements
    length: "",
    width: "",
    depth: "",
    tunneling: "",
    undermining: "",
    
    // Characteristics
    woundType: "",
    stage: "",
    woundBed: [],
    woundBedPercentages: {},
    drainageAmount: "none",
    drainageType: "",
    drainageColor: "",
    odor: "none",
    surroundingSkin: [],
    woundEdges: "",
    
    // Pain
    painScore: 0,
    painAtRest: false,
    painWithDressing: false,
    painCharacteristics: "",
    
    // Infection
    infectionSigns: false,
    infectionIndicators: [],
    providerNotified: false,
    notificationDate: "",
    
    // Treatment
    dressingType: "",
    dressingFrequency: "",
    cleansingSolution: "",
    additionalTreatments: [],
    suppliesNeeded: [],
    
    // Hospice context
    woundFocus: "",
    terminalDiagnosisLink: "",
    functionalStatusLink: "",
    nutritionalStatus: "",
    
    // Comparison
    comparisonToPrior: "new",
    woundOnsetDate: "",
    notes: ""
  });
  
  // Generated note
  const [generatedNote, setGeneratedNote] = useState("");
  const [complianceAlerts, setComplianceAlerts] = useState([]);

  // Handle image upload
  const handleImageUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a JPEG, PNG, or WebP image");
      return;
    }
    
    // Read file as base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result.split(",")[1];
      setImageData({
        base64,
        mimeType: file.type,
        fileName: file.name
      });
      setImagePreview(event.target.result);
    };
    reader.readAsDataURL(file);
  }, []);

  // Handle camera capture
  const handleCameraCapture = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      
      // Create video element
      const video = document.createElement("video");
      video.srcObject = stream;
      video.play();
      
      // Wait for video to load
      await new Promise((resolve) => {
        video.onloadedmetadata = resolve;
      });
      
      // Create canvas and capture frame
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0);
      
      // Stop stream
      stream.getTracks().forEach(track => track.stop());
      
      // Get base64
      const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
      const base64 = dataUrl.split(",")[1];
      
      setImageData({
        base64,
        mimeType: "image/jpeg",
        fileName: "camera_capture.jpg"
      });
      setImagePreview(dataUrl);
      
      toast.success("Image captured successfully");
    } catch (error) {
      toast.error("Could not access camera. Please upload an image instead.");
    }
  }, []);

  // Analyze image with AI
  const analyzeImage = async () => {
    if (!imageData) {
      toast.error("Please upload or capture an image first");
      return;
    }
    
    setIsAnalyzing(true);
    try {
      const token = localStorage.getItem("hospice_token");
      const response = await fetch(`${API}/api/wound-assessment/analyze-image`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          patient_id: patientId,
          image_base64: imageData.base64,
          image_mime_type: imageData.mimeType
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Analysis failed");
      }
      
      const data = await response.json();
      setAiAnalysis(data);
      
      // Pre-fill form with AI suggestions
      if (data.wound_characteristics?.likely_type) {
        setFormData(prev => ({
          ...prev,
          woundType: data.wound_characteristics.likely_type.toLowerCase().replace(/ /g, "_")
        }));
      }
      
      toast.success("Image analyzed successfully");
      setActiveTab("document");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Update form field
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Toggle array field
  const toggleArrayField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  // Save assessment
  const saveAssessment = async () => {
    // Validate required fields
    if (!formData.length || !formData.width) {
      toast.error("Wound measurements are required");
      return;
    }
    if (formData.painScore === undefined) {
      toast.error("Pain assessment is required");
      return;
    }
    if (!formData.woundType) {
      toast.error("Wound type is required");
      return;
    }
    
    setIsSaving(true);
    try {
      const token = localStorage.getItem("hospice_token");
      
      const payload = {
        patient_id: patientId,
        location: {
          anatomical_site: formData.anatomicalSite,
          body_region: formData.bodyRegion,
          laterality: formData.laterality || null
        },
        measurements: {
          length_cm: parseFloat(formData.length),
          width_cm: parseFloat(formData.width),
          depth_cm: formData.depth ? parseFloat(formData.depth) : null,
          tunneling: formData.tunneling || null,
          undermining: formData.undermining || null
        },
        characteristics: {
          wound_type: formData.woundType,
          stage: formData.stage || null,
          wound_bed: formData.woundBed,
          wound_bed_percentages: Object.keys(formData.woundBedPercentages).length > 0 
            ? formData.woundBedPercentages : null,
          drainage_amount: formData.drainageAmount,
          drainage_type: formData.drainageType || null,
          drainage_color: formData.drainageColor || null,
          odor: formData.odor,
          surrounding_skin: formData.surroundingSkin,
          wound_edges: formData.woundEdges || null
        },
        pain: {
          pain_score: parseInt(formData.painScore),
          pain_at_rest: formData.painAtRest,
          pain_with_dressing_change: formData.painWithDressing,
          pain_characteristics: formData.painCharacteristics || null
        },
        infection: {
          signs_present: formData.infectionSigns,
          indicators: formData.infectionIndicators,
          provider_notified: formData.providerNotified,
          notification_date: formData.notificationDate || null
        },
        treatment: {
          dressing_type: formData.dressingType,
          dressing_frequency: formData.dressingFrequency,
          cleansing_solution: formData.cleansingSolution || null,
          additional_treatments: formData.additionalTreatments,
          supplies_needed: formData.suppliesNeeded
        },
        hospice_context: {
          wound_focus: formData.woundFocus,
          terminal_diagnosis_link: formData.terminalDiagnosisLink || null,
          functional_status_link: formData.functionalStatusLink || null,
          nutritional_status: formData.nutritionalStatus || null
        },
        comparison_to_prior: formData.comparisonToPrior,
        wound_onset_date: formData.woundOnsetDate || null,
        notes: formData.notes || null,
        image_reference: imageData?.fileName || null
      };
      
      const response = await fetch(`${API}/api/wound-assessment/assessments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (data.hard_stops) {
          setComplianceAlerts(data.hard_stops);
          toast.error("Cannot save - required information missing");
          return;
        }
        throw new Error(data.detail || "Save failed");
      }
      
      setGeneratedNote(data.generated_note);
      setComplianceAlerts(data.compliance_alerts || []);
      setActiveTab("note");
      
      toast.success("Wound assessment saved successfully");
      
      if (onComplete) {
        onComplete(data);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="wound-assessment">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="upload" className="flex items-center gap-2">
            <Camera className="w-4 h-4" />
            Image Upload
          </TabsTrigger>
          <TabsTrigger value="document" className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4" />
            Documentation
          </TabsTrigger>
          <TabsTrigger value="note" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Generated Note
          </TabsTrigger>
        </TabsList>

        {/* Image Upload Tab */}
        <TabsContent value="upload" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileImage className="w-5 h-5 text-blue-500" />
                Wound Image Upload
              </CardTitle>
              <CardDescription>
                Upload or capture a wound image for AI-assisted analysis
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Upload/Capture buttons */}
              <div className="flex gap-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Image
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleCameraCapture}
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Take Photo
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleImageUpload}
                />
              </div>

              {/* Image Preview */}
              {imagePreview && (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Wound preview"
                    className="w-full max-h-96 object-contain rounded-lg border"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 right-2 bg-black/50 hover:bg-black/70"
                    onClick={() => {
                      setImageData(null);
                      setImagePreview(null);
                      setAiAnalysis(null);
                    }}
                  >
                    <X className="w-4 h-4 text-white" />
                  </Button>
                </div>
              )}

              {/* Analyze Button */}
              {imageData && (
                <Button
                  className="w-full bg-violet-600 hover:bg-violet-700"
                  onClick={analyzeImage}
                  disabled={isAnalyzing}
                >
                  {isAnalyzing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing Image...
                    </>
                  ) : (
                    <>
                      <Brain className="w-4 h-4 mr-2" />
                      Analyze with AI
                    </>
                  )}
                </Button>
              )}

              {/* AI Analysis Results */}
              {aiAnalysis && (
                <Card className="bg-violet-50 border-violet-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-violet-700">
                      <Sparkles className="w-4 h-4" />
                      AI Analysis Results
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Observations */}
                    {aiAnalysis.clinical_observations?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-violet-600 mb-1">Clinical Observations:</p>
                        <ul className="text-sm space-y-1">
                          {aiAnalysis.clinical_observations.map((obs, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <CheckCircle className="w-3 h-3 mt-1 text-violet-500 flex-shrink-0" />
                              <span>{obs}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Documentation Prompts */}
                    {aiAnalysis.documentation_prompts?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-violet-600 mb-1">Please Confirm:</p>
                        <ul className="text-sm space-y-1">
                          {aiAnalysis.documentation_prompts.map((prompt, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <Info className="w-3 h-3 mt-1 text-blue-500 flex-shrink-0" />
                              <span>{prompt}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Warnings */}
                    {aiAnalysis.warnings?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-amber-600 mb-1">Warnings:</p>
                        <ul className="text-sm space-y-1">
                          {aiAnalysis.warnings.map((warning, i) => (
                            <li key={i} className="flex items-start gap-2 text-amber-700">
                              <AlertTriangle className="w-3 h-3 mt-1 flex-shrink-0" />
                              <span>{warning}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <Button 
                      className="w-full mt-2"
                      onClick={() => setActiveTab("document")}
                    >
                      Continue to Documentation
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documentation Tab */}
        <TabsContent value="document" className="space-y-4">
          {/* Location Section */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Wound Location</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="anatomicalSite">Anatomical Site *</Label>
                <Input
                  id="anatomicalSite"
                  placeholder="e.g., Sacrum, Left Heel"
                  value={formData.anatomicalSite}
                  onChange={(e) => updateField("anatomicalSite", e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="bodyRegion">Body Region *</Label>
                <Select value={formData.bodyRegion} onValueChange={(v) => updateField("bodyRegion", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select region" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="head_neck">Head/Neck</SelectItem>
                    <SelectItem value="trunk">Trunk</SelectItem>
                    <SelectItem value="upper_extremity">Upper Extremity</SelectItem>
                    <SelectItem value="lower_extremity">Lower Extremity</SelectItem>
                    <SelectItem value="sacral_coccygeal">Sacral/Coccygeal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="laterality">Laterality</Label>
                <Select value={formData.laterality} onValueChange={(v) => updateField("laterality", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left">Left</SelectItem>
                    <SelectItem value="right">Right</SelectItem>
                    <SelectItem value="bilateral">Bilateral</SelectItem>
                    <SelectItem value="midline">Midline</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Measurements Section */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Wound Measurements (cm)</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-5 gap-4">
              <div>
                <Label htmlFor="length">Length *</Label>
                <Input
                  id="length"
                  type="number"
                  step="0.1"
                  placeholder="cm"
                  value={formData.length}
                  onChange={(e) => updateField("length", e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="width">Width *</Label>
                <Input
                  id="width"
                  type="number"
                  step="0.1"
                  placeholder="cm"
                  value={formData.width}
                  onChange={(e) => updateField("width", e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="depth">Depth</Label>
                <Input
                  id="depth"
                  type="number"
                  step="0.1"
                  placeholder="cm"
                  value={formData.depth}
                  onChange={(e) => updateField("depth", e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="tunneling">Tunneling</Label>
                <Input
                  id="tunneling"
                  placeholder="e.g., 2cm at 3 o'clock"
                  value={formData.tunneling}
                  onChange={(e) => updateField("tunneling", e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="undermining">Undermining</Label>
                <Input
                  id="undermining"
                  placeholder="e.g., 1cm from 9-12"
                  value={formData.undermining}
                  onChange={(e) => updateField("undermining", e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Wound Type & Characteristics */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Wound Characteristics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Wound Type *</Label>
                  <Select value={formData.woundType} onValueChange={(v) => updateField("woundType", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select wound type" />
                    </SelectTrigger>
                    <SelectContent>
                      {WOUND_TYPES.map(type => (
                        <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {formData.woundType === "pressure_injury" && (
                  <div>
                    <Label>Stage *</Label>
                    <Select value={formData.stage} onValueChange={(v) => updateField("stage", v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select stage" />
                      </SelectTrigger>
                      <SelectContent>
                        {PRESSURE_STAGES.map(stage => (
                          <SelectItem key={stage.id} value={stage.id}>
                            {stage.name} - {stage.desc}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Wound Bed */}
              <div>
                <Label className="mb-2 block">Wound Bed Tissue Types</Label>
                <div className="flex flex-wrap gap-2">
                  {WOUND_BED_TYPES.map(type => (
                    <Badge
                      key={type.id}
                      variant={formData.woundBed.includes(type.id) ? "default" : "outline"}
                      className={`cursor-pointer ${formData.woundBed.includes(type.id) ? type.color : ""}`}
                      onClick={() => toggleArrayField("woundBed", type.id)}
                    >
                      {type.name}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Drainage */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Drainage Amount</Label>
                  <Select value={formData.drainageAmount} onValueChange={(v) => updateField("drainageAmount", v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DRAINAGE_AMOUNTS.map(amt => (
                        <SelectItem key={amt} value={amt}>{amt.charAt(0).toUpperCase() + amt.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {formData.drainageAmount !== "none" && (
                  <>
                    <div>
                      <Label>Drainage Type</Label>
                      <Select value={formData.drainageType} onValueChange={(v) => updateField("drainageType", v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {DRAINAGE_TYPES.map(type => (
                            <SelectItem key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Drainage Color</Label>
                      <Input
                        placeholder="e.g., Yellow-green"
                        value={formData.drainageColor}
                        onChange={(e) => updateField("drainageColor", e.target.value)}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Surrounding Skin */}
              <div>
                <Label className="mb-2 block">Surrounding Skin</Label>
                <div className="flex flex-wrap gap-2">
                  {SURROUNDING_SKIN.map(condition => (
                    <Badge
                      key={condition}
                      variant={formData.surroundingSkin.includes(condition) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => toggleArrayField("surroundingSkin", condition)}
                    >
                      {condition.charAt(0).toUpperCase() + condition.slice(1)}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Odor */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Odor</Label>
                  <Select value={formData.odor} onValueChange={(v) => updateField("odor", v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="mild">Mild</SelectItem>
                      <SelectItem value="moderate">Moderate</SelectItem>
                      <SelectItem value="strong">Strong</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Wound Edges</Label>
                  <Select value={formData.woundEdges} onValueChange={(v) => updateField("woundEdges", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="well-defined">Well-Defined</SelectItem>
                      <SelectItem value="irregular">Irregular</SelectItem>
                      <SelectItem value="rolled">Rolled/Epibole</SelectItem>
                      <SelectItem value="attached">Attached</SelectItem>
                      <SelectItem value="undermined">Undermined</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pain Assessment */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Pain Assessment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label>Pain Score (0-10) *</Label>
                  <Input
                    type="number"
                    min="0"
                    max="10"
                    value={formData.painScore}
                    onChange={(e) => updateField("painScore", e.target.value)}
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="painAtRest"
                    checked={formData.painAtRest}
                    onChange={(e) => updateField("painAtRest", e.target.checked)}
                  />
                  <Label htmlFor="painAtRest">Pain at Rest</Label>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="painWithDressing"
                    checked={formData.painWithDressing}
                    onChange={(e) => updateField("painWithDressing", e.target.checked)}
                  />
                  <Label htmlFor="painWithDressing">Pain with Dressing Change</Label>
                </div>
              </div>
              <div>
                <Label>Pain Characteristics</Label>
                <Input
                  placeholder="e.g., Burning, aching, sharp"
                  value={formData.painCharacteristics}
                  onChange={(e) => updateField("painCharacteristics", e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Infection Screening */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                Infection Screening
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="infectionSigns"
                  checked={formData.infectionSigns}
                  onChange={(e) => updateField("infectionSigns", e.target.checked)}
                />
                <Label htmlFor="infectionSigns" className="text-amber-600 font-medium">
                  Signs of Infection Present
                </Label>
              </div>
              
              {formData.infectionSigns && (
                <>
                  <div>
                    <Label className="mb-2 block">Infection Indicators</Label>
                    <div className="flex flex-wrap gap-2">
                      {["erythema", "warmth", "swelling", "purulent_drainage", "fever", "increased_pain", "cellulitis"].map(ind => (
                        <Badge
                          key={ind}
                          variant={formData.infectionIndicators.includes(ind) ? "destructive" : "outline"}
                          className="cursor-pointer"
                          onClick={() => toggleArrayField("infectionIndicators", ind)}
                        >
                          {ind.replace("_", " ").charAt(0).toUpperCase() + ind.replace("_", " ").slice(1)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="providerNotified"
                        checked={formData.providerNotified}
                        onChange={(e) => updateField("providerNotified", e.target.checked)}
                      />
                      <Label htmlFor="providerNotified">Provider Notified</Label>
                    </div>
                    {formData.providerNotified && (
                      <div>
                        <Input
                          type="date"
                          value={formData.notificationDate}
                          onChange={(e) => updateField("notificationDate", e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                  {!formData.providerNotified && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Provider notification required for signs of infection
                      </p>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Treatment Plan */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Treatment Plan</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Dressing Type *</Label>
                  <Input
                    placeholder="e.g., Foam, Hydrocolloid"
                    value={formData.dressingType}
                    onChange={(e) => updateField("dressingType", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Dressing Frequency *</Label>
                  <Input
                    placeholder="e.g., Daily, Q3 days, PRN"
                    value={formData.dressingFrequency}
                    onChange={(e) => updateField("dressingFrequency", e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label>Cleansing Solution</Label>
                <Input
                  placeholder="e.g., Normal Saline, Wound Cleanser"
                  value={formData.cleansingSolution}
                  onChange={(e) => updateField("cleansingSolution", e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Hospice Context */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Hospice Clinical Context</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Wound Treatment Focus *</Label>
                <Select value={formData.woundFocus} onValueChange={(v) => updateField("woundFocus", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select focus" />
                  </SelectTrigger>
                  <SelectContent>
                    {HOSPICE_FOCUS.map(focus => (
                      <SelectItem key={focus.id} value={focus.id}>
                        {focus.name} - {focus.desc}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Terminal Diagnosis Correlation</Label>
                  <Textarea
                    placeholder="How wound relates to terminal diagnosis..."
                    value={formData.terminalDiagnosisLink}
                    onChange={(e) => updateField("terminalDiagnosisLink", e.target.value)}
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Functional/Nutritional Status</Label>
                  <Textarea
                    placeholder="PPS score, nutritional impact..."
                    value={formData.nutritionalStatus}
                    onChange={(e) => updateField("nutritionalStatus", e.target.value)}
                    rows={2}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Comparison & Notes */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Assessment Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Comparison to Prior</Label>
                  <Select value={formData.comparisonToPrior} onValueChange={(v) => updateField("comparisonToPrior", v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New Wound (Baseline)</SelectItem>
                      <SelectItem value="improved">Improved</SelectItem>
                      <SelectItem value="stable">Stable</SelectItem>
                      <SelectItem value="declined">Declined</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Wound Onset Date</Label>
                  <Input
                    type="date"
                    value={formData.woundOnsetDate}
                    onChange={(e) => updateField("woundOnsetDate", e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label>Additional Clinical Notes</Label>
                <Textarea
                  placeholder="Additional observations, interventions, or clinical context..."
                  value={formData.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Compliance Alerts */}
          {complianceAlerts.length > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="pt-4">
                <p className="font-medium text-amber-700 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Compliance Alerts
                </p>
                <ul className="space-y-1">
                  {complianceAlerts.map((alert, i) => (
                    <li key={i} className="text-sm text-amber-600">{alert}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Save Button */}
          <Button
            className="w-full bg-teal-600 hover:bg-teal-700"
            onClick={saveAssessment}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving Assessment...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Save Wound Assessment
              </>
            )}
          </Button>
        </TabsContent>

        {/* Generated Note Tab */}
        <TabsContent value="note" className="space-y-4">
          {generatedNote ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-teal-500" />
                  Generated Wound Assessment Note
                </CardTitle>
                <CardDescription>
                  Review and copy this note to the patient chart
                </CardDescription>
              </CardHeader>
              <CardContent>
                <pre className="whitespace-pre-wrap text-sm bg-slate-50 p-4 rounded-lg border font-mono">
                  {generatedNote}
                </pre>
                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(generatedNote);
                      toast.success("Note copied to clipboard");
                    }}
                  >
                    Copy to Clipboard
                  </Button>
                  {onClose && (
                    <Button onClick={onClose}>
                      Done
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-8 text-center text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Complete the documentation form to generate the wound note</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
